import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { endpoints } from '../api';
import type {
  BusinessLedgerEntry, BusinessQuery, BusinessReport, BusinessScope,
} from '../business-types';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Stat, Stats, Status } from '../components';
import { BusinessCashBars, BusinessTrend } from './business-charts';

type PeriodPreset = 7 | 30 | 90 | 'all' | 'custom';
type DirectionFilter = 'all' | 'income' | 'expense' | 'unknown';

const DATE_FORMAT = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
const LEDGER_DATE_FORMAT = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' });
const INTEGER_FORMAT = new Intl.NumberFormat('es-ES', { useGrouping: 'always' });
const MONEY_FORMAT = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });

function timezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
}

function localISO(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function rangeFor(days: number): Pick<BusinessQuery, 'start' | 'end'> {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days + 1);
  return { start: localISO(start), end: localISO(end) };
}

function money(value: number | null | undefined, signed = false) {
  if (value === null || value === undefined) return 'Desconocido';
  const prefix = signed && value > 0 ? '+' : '';
  return `${prefix}${MONEY_FORMAT.format(value)} ISK`;
}

function compact(value: number | null | undefined, signed = false) {
  if (value === null || value === undefined) return 'Sin datos';
  return `${signed && value > 0 ? '+' : ''}${new Intl.NumberFormat('es-ES', { notation: 'compact', maximumFractionDigits: 1 }).format(value)} ISK`;
}

function formatDate(value: string | null | undefined, withTime = false) {
  if (!value) return 'Sin datos';
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  if (Number.isNaN(parsed.getTime())) return value;
  return (withTime ? DATE_TIME_FORMAT : DATE_FORMAT).format(parsed);
}

function readableDescription(value: string) {
  if (!value || !/[<&]/.test(value) || typeof DOMParser === 'undefined') return value;
  const document = new DOMParser().parseFromString(value, 'text/html');
  return document.body.textContent?.replace(/\s+/g, ' ').trim() || value;
}

function periodLabel(report: BusinessReport | null) {
  if (!report) return '';
  return `${formatDate(report.period.start)} — ${formatDate(report.period.end)}`;
}

function compareLabel(current: number, report: BusinessReport | null, field: 'income' | 'expenses' | 'netCashflow') {
  const previous = report?.previousPeriod;
  if (!previous?.hasData) return 'Sin periodo comparable';
  const baseline = previous[field];
  if (baseline === 0) return current === 0 ? 'Igual que el periodo anterior' : 'Periodo anterior: 0 ISK';
  const percent = ((current - baseline) / Math.abs(baseline)) * 100;
  return `${percent >= 0 ? '+' : ''}${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 }).format(percent)} % vs. periodo anterior`;
}

function useStableBusinessQuery(loader: () => Promise<BusinessReport>) {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const mounted = useRef(false);
  const sequence = useRef(0);
  const [data, setData] = useState<BusinessReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    const request = ++sequence.current;
    if (mounted.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const result = await loaderRef.current();
      if (mounted.current && request === sequence.current) setData(result);
    } catch (caught) {
      if (mounted.current && request === sequence.current) setError(caught instanceof Error ? caught : new Error('Error desconocido'));
    } finally {
      if (mounted.current && request === sequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, [reload]);

  return { data, loading, error, reload };
}

function useBusinessData(initialDays: 7 | 30 | 90) {
  const zone = useMemo(timezone, []);
  const [preset, setPresetState] = useState<PeriodPreset>(initialDays);
  const [query, setQuery] = useState<BusinessQuery>(() => ({ ...rangeFor(initialDays), scope: 'all', timezone: zone }));
  const [custom, setCustom] = useState(() => rangeFor(initialDays));
  const request = useStableBusinessQuery(() => endpoints.businessReport(query));
  const signature = JSON.stringify(query);
  const initialSignature = useRef(signature);

  useEffect(() => {
    if (initialSignature.current === signature) return;
    initialSignature.current = signature;
    void request.reload();
  }, [request.reload, signature]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void request.reload();
    }, 60000);
    return () => window.clearInterval(timer);
  }, [request.reload]);

  function setPreset(next: PeriodPreset) {
    setPresetState(next);
    if (next === 'custom') return;
    if (next === 'all') {
      const first = request.data?.coverage.firstEntryAt;
      const fallback = rangeFor(30);
      const start = first ? localISO(new Date(first)) : fallback.start;
      setCustom({ start, end: localISO(new Date()) });
      setQuery((current) => ({ start, end: localISO(new Date()), scope: current.scope, timezone: zone }));
      return;
    }
    const range = rangeFor(next);
    setCustom(range);
    setQuery((current) => ({ ...range, scope: current.scope, timezone: zone }));
  }

  function applyCustom(event: FormEvent) {
    event.preventDefault();
    if (!custom.start || !custom.end || custom.start > custom.end) return;
    setPresetState('custom');
    setQuery((current) => ({ ...custom, scope: current.scope, timezone: zone }));
  }

  function setScope(scope: BusinessScope) {
    setQuery((current) => ({ ...current, scope }));
  }

  return { ...request, preset, setPreset, query, setScope, custom, setCustom, applyCustom };
}

function PeriodControls({ state }: { state: ReturnType<typeof useBusinessData> }) {
  return <div className="filters">
    <div className="segments" role="group" aria-label="Periodo">
      {([7, 30, 90, 'all'] as const).map((value) => <button type="button" aria-pressed={state.preset === value} disabled={value === 'all' && !state.data} onClick={() => state.setPreset(value)} key={value}>{value === 'all' ? 'Todo' : `${value} días`}</button>)}
      <button type="button" aria-pressed={state.preset === 'custom'} onClick={() => state.setPreset('custom')}>Fechas</button>
    </div>
    <div className="segments" role="group" aria-label="Alcance financiero">
      <button type="button" aria-pressed={(state.query.scope ?? 'all') === 'all'} onClick={() => state.setScope('all')}>Toda la actividad</button>
      <button type="button" aria-pressed={state.query.scope === 'business'} onClick={() => state.setScope('business')}>Mercado e industria</button>
    </div>
    {state.preset === 'custom' && <form className="filters" onSubmit={state.applyCustom}>
      <Field label="Desde"><input type="date" required value={state.custom.start} onChange={(event) => state.setCustom((current) => ({ ...current, start: event.target.value }))} /></Field>
      <Field label="Hasta"><input type="date" required value={state.custom.end} onChange={(event) => state.setCustom((current) => ({ ...current, end: event.target.value }))} /></Field>
      <Button type="submit" variant="secondary" disabled={!state.custom.start || !state.custom.end || state.custom.start > state.custom.end}>Aplicar</Button>
    </form>}
    <small role="status" style={{ marginLeft: 'auto', alignSelf: 'center' }}>{state.loading ? 'Actualizando…' : `${periodLabel(state.data)}${state.data ? ` · ${state.data.period.timezone}` : ''}`}</small>
  </div>;
}

function ReportActions({ reload }: { reload: () => Promise<void> }) {
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  async function synchronize() {
    setSyncing(true);
    setMessage(null);
    try {
      const result = await endpoints.startSync('character');
      setMessage(result.status === 'queued' ? 'En cola' : result.detail);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'No se pudo solicitar la sincronización');
    } finally {
      setSyncing(false);
    }
  }
  return <>
    {message && <small role="status">{message}</small>}
    <a className="button button--ghost" href="/api/documentation/business" target="_blank" rel="noreferrer">Guía ↗</a>
    <Button variant="ghost" onClick={() => void reload()}>Actualizar</Button>
    <Button variant="secondary" onClick={() => void synchronize()} disabled={syncing}>{syncing ? 'Solicitando…' : 'Sincronizar con EVE'}</Button>
  </>;
}

export function BusinessPage() {
  const state = useBusinessData(7);
  const report = state.data;
  const [trendMode, setTrendMode] = useState<'balance' | 'cashflow'>('balance');
  const summary = report?.summary;
  const topCategories = report?.categories.slice().sort((a, b) => (b.income + b.expenses) - (a.income + a.expenses)) ?? [];
  const maxCategory = Math.max(1, ...topCategories.map((category) => category.income + category.expenses));
  const products = report?.products.filter((product) => product.soldQuantity > 0).sort((a, b) => b.salesGross - a.salesGross).slice(0, 8) ?? [];
  const recent = report?.ledger.slice().sort((a, b) => b.date.localeCompare(a.date)).slice(0, 8) ?? [];

  return <Page title="Negocio" actions={<ReportActions reload={state.reload} />}>
    <PeriodControls state={state} />
    {state.loading && !report && <LoadingState />}
    {state.error && !report && <ErrorState error={state.error} onRetry={() => void state.reload()} />}
    {state.error && report && <Note tone="error">No se pudo actualizar: {state.error.message}. Se conservan los últimos datos.</Note>}
    {report && <>
      <Stats label="Resumen financiero">
        <Stat label="Wallet" value={money(summary?.walletBalance)} detail="Último saldo de EVE, no del periodo" />
        <Stat label="Flujo neto" value={money(summary?.netCashflow, true)} detail={`${compareLabel(summary?.netCashflow ?? 0, report, 'netCashflow')}${summary?.transfersNet ? ` · Transferencias ${money(summary.transfersNet, true)}` : ''}`} tone={(summary?.netCashflow ?? 0) >= 0 ? 'pos' : 'neg'} />
        <Stat label="Ingresos" value={money(summary?.income)} detail={compareLabel(summary?.income ?? 0, report, 'income')} />
        <Stat label="Gastos" value={money(summary?.expenses)} detail={compareLabel(summary?.expenses ?? 0, report, 'expenses')} />
        <Stat label="Beneficio atribuido" value={money(summary?.realizedProfit)} detail={summary?.realizedProfit === null ? <>No se estima: faltan <a className="link" href="#/finance-records">costes de lote</a></> : `${INTEGER_FORMAT.format(summary?.attributedSales ?? 0)} ventas con coste`} />
      </Stats>

      <Section title={trendMode === 'balance' ? 'Saldo de la wallet' : 'Flujo acumulado'} action={<div className="segments"><button type="button" aria-pressed={trendMode === 'balance'} onClick={() => setTrendMode('balance')}>Wallet</button><button type="button" aria-pressed={trendMode === 'cashflow'} onClick={() => setTrendMode('cashflow')}>Flujo</button></div>}>
        {report.timeline.length > 0 && (trendMode === 'balance' || report.summary.entryCount > 0) ? <BusinessTrend days={report.timeline} mode={trendMode} /> : <EmptyState title="Sin movimientos en este periodo" detail=" Amplía el periodo o sincroniza tu piloto." />}
      </Section>

      <div className="split">
        <Section title="Entradas y salidas por día">
          {report.summary.entryCount > 0 ? <BusinessCashBars days={report.timeline} /> : <EmptyState title="Sin asientos en el periodo" />}
        </Section>
        <Section title={`Por categoría · ${INTEGER_FORMAT.format(summary?.entryCount ?? 0)} asientos`}>
          {topCategories.length === 0 ? <EmptyState title="Sin categorías" /> : <ul className="bars">{topCategories.map((category) => <li key={category.id}>
            <strong>{category.label}</strong>
            <span className="mono">{compact(category.income + category.expenses)}</span>
            <small>{INTEGER_FORMAT.format(category.count)} mov. · neto <span className={category.net >= 0 ? 'pos' : 'neg'}>{compact(category.net, true)}</span></small>
            <i style={{ width: `${((category.income + category.expenses) / maxCategory) * 100}%` }} />
          </li>)}</ul>}
        </Section>
      </div>

      <Section title="Productos vendidos">
        <Stats label="Totales comerciales del periodo">
          <Stat label="Ventas brutas" value={money(summary?.salesGross)} />
          <Stat label="Compras brutas" value={money(summary?.purchasesGross)} />
          <Stat label="Comisiones de mercado" value={money(summary?.marketFees)} />
          <Stat label="Costes industriales" value={money(summary?.industrialCosts)} />
        </Stats>
        {products.length === 0 ? <EmptyState title="Sin productos identificados" detail=" Puede haber asientos que EVE no permite enlazar con una transacción." /> : <div className="table-wrap"><table className="table--stack"><thead><tr><th>Producto</th><th className="num">Unidades</th><th className="num">Ventas brutas</th><th className="num">Precio medio</th><th className="num">Comprado</th></tr></thead><tbody>{products.map((product) => <tr key={product.typeId}>
          <td className="wide"><strong>{product.typeName}</strong></td>
          <td className="num" data-label="Unidades">{INTEGER_FORMAT.format(product.soldQuantity)}</td>
          <td className="num" data-label="Ventas brutas">{money(product.salesGross)}</td>
          <td className="num" data-label="Precio medio">{money(product.averageSalePrice)}</td>
          <td className="num" data-label="Comprado">{product.purchasedQuantity > 0 ? `${INTEGER_FORMAT.format(product.purchasedQuantity)} · ${money(product.purchasesGross)}` : '—'}</td>
        </tr>)}</tbody></table></div>}
      </Section>

      <Section title="Últimos movimientos" action={<a className="link" href="#/ledger">Ledger completo</a>}>
        {recent.length === 0 ? <EmptyState title="Sin movimientos en este periodo" /> : <ul className="feed">{recent.map((entry) => <li key={entry.id}>
          <strong>{readableDescription(entry.description) || entry.categoryLabel}</strong>
          <small>{formatDate(entry.date, true)} · {entry.categoryLabel}{entry.items.length > 0 ? ` · ${entry.items.map((item) => item.typeName).join(', ')}` : ''}</small>
          <span className={`feed-end mono ${amountClass(entry.amount)}`}>{money(entry.amount, true)}</span>
        </li>)}</ul>}
      </Section>
      <Coverage report={report} />
    </>}
  </Page>;
}

function amountClass(amount: number | null) {
  return amount === null ? 'muted' : amount >= 0 ? 'pos' : 'neg';
}

function Coverage({ report }: { report: BusinessReport }) {
  const statusLabels: Record<string, string> = { current: 'Vigente', stale: 'Caducada', missing_scope: 'Sin autorización', pending: 'Pendiente', error: 'Con error' };
  return <details>
    <summary>Cobertura de los datos</summary>
    <div className="stack">
      <p className="fine">{report.coverage.notice}</p>
      <p className="fine">Disponible: {formatDate(report.coverage.firstEntryAt, true)} — {formatDate(report.coverage.lastEntryAt, true)} · {INTEGER_FORMAT.format(report.coverage.totalJournalEntries)} asientos · {INTEGER_FORMAT.format(report.coverage.totalTransactions)} transacciones · {INTEGER_FORMAT.format(report.coverage.unmatchedTransactions)} sin enlazar.</p>
      <div className="table-wrap"><table><tbody>{report.coverage.sources.map((source) => <tr key={`${source.module}-${source.label}`}>
        <td><strong>{source.label}</strong></td>
        <td><Status value={source.status} label={statusLabels[source.status] ?? source.status.replaceAll('_', ' ')} /></td>
        <td className="muted">{source.detail}</td>
        <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(source.lastSyncAt, true)}</td>
      </tr>)}</tbody></table></div>
    </div>
  </details>;
}

function directionOf(entry: BusinessLedgerEntry): Exclude<DirectionFilter, 'all'> {
  if (entry.amount === null) return 'unknown';
  return entry.amount >= 0 ? 'income' : 'expense';
}

function csvText(value: string, forceText = false) {
  let safe = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (forceText || /(^|\n)\s*[=+\-@]/.test(safe)) safe = `'${safe}`;
  return `"${safe.replaceAll('"', '""')}"`;
}

function downloadLedger(entries: BusinessLedgerEntry[], report: BusinessReport) {
  const columns = ['id', 'journal_id', 'fecha', 'categoria', 'alcance', 'ref_type', 'descripcion', 'importe_isk', 'saldo_isk', 'first_party_id', 'second_party_id', 'items'];
  const rows = entries.map((entry) => [
    csvText(entry.id, true), csvText(entry.sourceId, true), csvText(entry.date), csvText(entry.categoryLabel), csvText(entry.scope), csvText(entry.refType), csvText(readableDescription(entry.description)),
    entry.amount === null ? '' : String(entry.amount), entry.balance === null ? '' : String(entry.balance),
    entry.firstPartyId === null ? '' : csvText(entry.firstPartyId, true), entry.secondPartyId === null ? '' : csvText(entry.secondPartyId, true),
    csvText(entry.items.map((item) => `${item.side}:${item.typeName} x${item.quantity} @ ${item.unitPrice}`).join(' | ')),
  ]);
  const csv = `\uFEFF${columns.map((column) => csvText(column)).join(',')}\n${rows.map((row) => row.join(',')).join('\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `ledger-${report.period.start}-${report.period.end}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function LedgerPage() {
  const state = useBusinessData(7);
  const report = state.data;
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const ledger = useMemo(() => report?.ledger ?? [], [report]);
  const categories = useMemo(() => Array.from(new Map(ledger.map((entry) => [entry.category, entry.categoryLabel])).entries()).sort((a, b) => a[1].localeCompare(b[1], 'es')), [ledger]);
  const normalizedSearch = search.trim().toLocaleLowerCase('es');
  const filtered = useMemo(() => ledger.filter((entry) => {
    const day = localISO(new Date(entry.date));
    if (from && day < from) return false;
    if (to && day > to) return false;
    if (category !== 'all' && entry.category !== category) return false;
    if (direction !== 'all' && directionOf(entry) !== direction) return false;
    if (!normalizedSearch) return true;
    const haystack = [entry.id, entry.sourceId, entry.refType, readableDescription(entry.description), entry.categoryLabel, entry.firstPartyId, entry.secondPartyId, ...entry.items.flatMap((item) => [item.transactionId, item.typeName, item.locationName])].filter(Boolean).join(' ').toLocaleLowerCase('es');
    return haystack.includes(normalizedSearch);
  }), [ledger, from, to, category, direction, normalizedSearch]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const shown = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const known = filtered.filter((entry) => entry.amount !== null);
  const filteredIncome = known.reduce((total, entry) => total + Math.max(entry.amount ?? 0, 0), 0);
  const filteredExpenses = known.reduce((total, entry) => total + Math.abs(Math.min(entry.amount ?? 0, 0)), 0);
  const unknownAmounts = filtered.length - known.length;

  useEffect(() => { setPage(1); }, [search, category, direction, from, to, pageSize]);

  function resetFilters() {
    setSearch(''); setCategory('all'); setDirection('all'); setFrom(''); setTo(''); setPage(1);
  }

  function toggle(id: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return <Page title="Ledger" actions={<ReportActions reload={state.reload} />}>
    {state.loading && !report && <LoadingState />}
    {state.error && !report && <ErrorState error={state.error} onRetry={() => void state.reload()} />}
    {state.error && report && <Note tone="error">No se pudo actualizar: {state.error.message}. Se conservan los últimos datos.</Note>}
    {report && <Section className="ledger-notebook">
      <div className="row">
        <p className="fine" role="status">Cuaderno contable · {periodLabel(report)}{state.loading ? ' · Actualizando…' : ''}</p>
        <p className="fine">Saldo actual <strong className="mono">{money(report.summary.walletBalance)}</strong></p>
      </div>
      <div className="filters ledger-toolbar">
        <Field label="Buscar apunte" wide><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Concepto u objeto" /></Field>
        <Button variant="secondary" disabled={state.loading || Boolean(state.error) || filtered.length === 0} onClick={() => downloadLedger(filtered, report)}>Exportar CSV</Button>
      </div>
      <details className="ledger-options">
        <summary>Periodo y filtros{category !== 'all' || direction !== 'all' || from || to || state.query.scope === 'business' ? ' · Activos' : ''}</summary>
        <div className="stack">
          <PeriodControls state={state} />
          <div className="filters">
            <Field label="Desde"><input type="date" value={from} min={report.period.start} max={report.period.end} onChange={(event) => setFrom(event.target.value)} /></Field>
            <Field label="Hasta"><input type="date" value={to} min={report.period.start} max={report.period.end} onChange={(event) => setTo(event.target.value)} /></Field>
            <Field label="Categoría"><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">Todas</option>{categories.map(([id, label]) => <option value={id} key={id}>{label}</option>)}</select></Field>
            <Field label="Dirección"><select value={direction} onChange={(event) => setDirection(event.target.value as DirectionFilter)}><option value="all">Todas</option><option value="income">Entradas</option><option value="expense">Salidas</option><option value="unknown">Importe desconocido</option></select></Field>
            <Button variant="ghost" onClick={resetFilters}>Limpiar</Button>
          </div>
        </div>
      </details>
      <p className="fine" id="ledger-balance-note">ISK · Debe: entradas · Haber: salidas · Más recientes primero. Resto conserva el saldo real al filtrar.</p>
      {unknownAmounts > 0 && <p className="fine">{INTEGER_FORMAT.format(unknownAmounts)} apuntes sin importe conocido; no se incluyen en los totales.</p>}
      {filtered.length === 0 ? <EmptyState title="Ningún movimiento coincide" action={<Button variant="secondary" onClick={resetFilters}>Limpiar filtros</Button>} /> : <div className="table-wrap"><table className="ledger-notebook-table" aria-label="Cuaderno contable" aria-describedby="ledger-balance-note"><colgroup><col className="ledger-date-column" /><col /><col className="ledger-money-column" /><col className="ledger-money-column" /><col className="ledger-balance-column" /></colgroup><thead><tr><th scope="col">Fecha</th><th scope="col">Apunte</th><th scope="col" className="num" title="Entradas">Debe</th><th scope="col" className="num" title="Salidas">Haber</th><th scope="col" className="num" title="Saldo tras el apunte">Resto</th></tr></thead><tbody>{shown.map((entry) => {
        const isExpanded = expanded.has(entry.id);
        return <Fragment key={entry.id}><tr className={isExpanded ? 'is-open' : undefined}>
          <td className="muted"><time dateTime={entry.date} title={formatDate(entry.date, true)}>{LEDGER_DATE_FORMAT.format(new Date(entry.date))}</time></td>
          <td><div className="ledger-entry">
            <button className="expand" type="button" aria-expanded={isExpanded} aria-controls={`ledger-detail-${entry.id}`} aria-label={`${isExpanded ? 'Cerrar' : 'Abrir'} detalle de ${readableDescription(entry.description) || 'Movimiento sin descripción'}`} onClick={() => toggle(entry.id)}>▶</button>
            <strong title={readableDescription(entry.description) || 'Movimiento sin descripción'}>{readableDescription(entry.description) || 'Movimiento sin descripción'}</strong>
          </div></td>
          <td className="num pos">{entry.amount !== null && entry.amount >= 0 ? MONEY_FORMAT.format(entry.amount) : <span className="muted" title={entry.amount === null ? 'Importe desconocido' : undefined}>—</span>}</td>
          <td className="num neg">{entry.amount !== null && entry.amount < 0 ? MONEY_FORMAT.format(Math.abs(entry.amount)) : <span className="muted" title={entry.amount === null ? 'Importe desconocido' : undefined}>—</span>}</td>
          <td className="num">{entry.balance === null ? <span className="muted" aria-label="Saldo desconocido" title="Saldo desconocido">—</span> : MONEY_FORMAT.format(entry.balance)}</td>
        </tr>{isExpanded && <tr className="detail-row" id={`ledger-detail-${entry.id}`}><td colSpan={5}><div className="detail">
          <p className="fine">{readableDescription(entry.description) || 'Movimiento sin descripción'}</p>
          <dl className="meta">
            <div><dt>Importe</dt><dd>{money(entry.amount, true)}</dd></div>
            <div><dt>Categoría</dt><dd>{entry.categoryLabel}</dd></div>
            <div><dt>Journal ID</dt><dd>{entry.sourceId}</dd></div>
            <div><dt>ID interno</dt><dd>{entry.id}</dd></div>
            <div><dt>Referencia</dt><dd>{entry.refType || 'Sin referencia'}</dd></div>
            <div><dt>Alcance</dt><dd>{entry.scope === 'business' ? 'Mercado e industria' : entry.scope === 'transfer' ? 'Transferencia' : 'Otra actividad'}</dd></div>
            <div><dt>First party</dt><dd>{entry.firstPartyId ?? 'Desconocido'}</dd></div>
            <div><dt>Second party</dt><dd>{entry.secondPartyId ?? 'Desconocido'}</dd></div>
          </dl>
          {entry.items.length > 0 ? <div><p className="fine">Transacciones asociadas. Explican el asiento; no son importes adicionales.</p><div className="table-wrap"><table><thead><tr><th>Objeto</th><th>Operación</th><th className="num">Cantidad</th><th className="num">Precio</th><th className="num">Bruto</th><th>Ubicación</th><th>Transacción</th></tr></thead><tbody>{entry.items.map((item) => <tr key={item.transactionId}>
            <td><strong>{item.typeName}</strong></td>
            <td>{item.side === 'sale' ? 'Venta' : 'Compra'}</td>
            <td className="num">{INTEGER_FORMAT.format(item.quantity)}</td>
            <td className="num">{money(item.unitPrice)}</td>
            <td className="num">{money(item.gross)}</td>
            <td>{item.locationName || item.locationId}</td>
            <td className="mono muted">{item.transactionId}</td>
          </tr>)}</tbody></table></div></div> : <p className="fine">Sin transacción de objeto enlazada.</p>}
        </div></td></tr>}</Fragment>;
      })}</tbody><tfoot><tr><th scope="row" colSpan={2}>Total filtrado · {INTEGER_FORMAT.format(filtered.length)} apuntes</th><td className="num pos">{MONEY_FORMAT.format(filteredIncome)}</td><td className="num neg">{MONEY_FORMAT.format(filteredExpenses)}</td><td className="num"><span className="muted" aria-label="Los saldos no se suman">—</span></td></tr></tfoot></table></div>}
      {filtered.length > 0 && <footer className="pager">
        <label>Filas <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}><option value={25}>25</option><option value={50}>50</option></select></label>
        <span>{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}</span>
        <div><Button variant="ghost" disabled={currentPage <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</Button><Button variant="ghost" disabled={currentPage >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Siguiente</Button></div>
      </footer>}
      <Coverage report={report} />
    </Section>}
  </Page>;
}

export function BusinessDailyFlow() {
  const state = useBusinessData(30);
  const report = state.data;
  return <Section title="Entradas y salidas · 30 días" action={<a className="link" href="#/finance">Negocio</a>}>
    {state.loading && !report && <LoadingState />}
    {state.error && !report && <ErrorState error={state.error} onRetry={() => void state.reload()} />}
    {report && <BusinessCashBars days={report.timeline} labels={['Entradas', 'Salidas']} />}
  </Section>;
}

export function BusinessOverview() {
  const state = useBusinessData(7);
  const report = state.data;
  return <Section title="Wallet · 7 días" action={<a className="link" href="#/finance">Negocio</a>}>
    {state.loading && !report && <LoadingState />}
    {state.error && !report && <ErrorState error={state.error} onRetry={() => void state.reload()} />}
    {report && <>
      <div className="row"><span className="mono">{compact(report.summary.walletBalance)}</span><span className="muted">Flujo <span className={`mono ${report.summary.netCashflow >= 0 ? 'pos' : 'neg'}`}>{compact(report.summary.netCashflow, true)}</span> · {INTEGER_FORMAT.format(report.summary.entryCount)} mov.</span></div>
      {report.timeline.length > 0 ? <BusinessTrend days={report.timeline} mode="balance" compact /> : <EmptyState title="Sin movimientos en estos 7 días" />}
    </>}
  </Section>;
}
